import React, { useState, FormEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  Container,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Logo } from '../components/Logo';
import axios from '../api/axios';

export const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const validateEmail = (value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value) {
      setEmailError(t('auth.login.emailRequired'));
      return false;
    }
    if (!emailRegex.test(value)) {
      setEmailError(t('auth.login.invalidEmail'));
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!validateEmail(email)) {
      return;
    }

    setLoading(true);

    try {
      await axios.post('/auth/forgot-password', { email });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || t('auth.forgotPassword.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #F5F5F0 0%, #E8E8DC 100%)',
        py: 4,
      }}
    >
      <Box sx={{ position: 'fixed', top: 24, right: 24 }}>
        <LanguageSwitcher />
      </Box>
      <Container maxWidth="sm">
        <Card
          sx={{
            boxShadow: '0 8px 32px rgba(74, 124, 89, 0.1)',
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ mb: 4, textAlign: 'center' }}>
              <Logo size="large" />
              <Typography
                variant="h5"
                sx={{ mt: 2, mb: 1, fontWeight: 600, color: 'text.primary' }}
              >
                {t('auth.forgotPassword.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('auth.forgotPassword.subtitle')}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success ? (
              <Alert severity="success" sx={{ mb: 3 }}>
                {t('auth.forgotPassword.success')}
              </Alert>
            ) : (
              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label={t('common.fields.email')}
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) validateEmail(e.target.value);
                  }}
                  onBlur={() => validateEmail(email)}
                  error={!!emailError}
                  helperText={emailError}
                  margin="normal"
                  required
                  autoComplete="email"
                  autoFocus
                  disabled={loading}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    mt: 3,
                    mb: 2,
                    py: 1.5,
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #4A7C59 0%, #355A40 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #355A40 0%, #2A4833 100%)',
                    },
                  }}
                >
                  {loading ? t('auth.forgotPassword.submitting') : t('auth.forgotPassword.submit')}
                </Button>
              </form>
            )}

            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                <Link
                  component={RouterLink}
                  to="/login"
                  sx={{
                    color: 'primary.main',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('auth.forgotPassword.backToLogin')}
                </Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
